<template>
    <main-dialog
        ref="dialog"
        heading="planning.copySubCampaign.heading"
        btn-confirm-text="actions.copy"
        :confirm-btn-disabled="!hasValidCopyWindow"
        :has-activator="hasActivator"
        @confirm="copySubCampaignDialogConfirm"
        @close="closeDialog"
    >
        <template v-if="hasActivator" v-slot:actions="{ activator }">
            <icon v-bind="activator" icon-name="custom:duplicate" small @click.stop />
        </template>
        <template v-slot:default>
            <div class="copy-sub-campaign">
                <p class="copy-sub-campaign__description">
                    {{ toSentenceCase($t('planning.copySubCampaign.description')) }}
                </p>
                <p v-if="!hasValidCopyWindow" class="copy-sub-campaign__error">
                    {{ $t(copyWindowError.messageKey, copyWindowError.messageParams) }}
                </p>
                <p class="copy-sub-campaign__form-item border">
                    <span class="copy-sub-campaign__form-item--label">
                        {{ toSentenceCase($t('entities.subCampaigns', 1)) }}
                    </span>
                    <vuex-checkbox
                        disabled
                        :getter="
                            () => modelsGetter({ model: copySubCampaignModels.subCampaignModel })
                        "
                        :setter="
                            value =>
                                modelsSetter({
                                    model: copySubCampaignModels.subCampaignModel,
                                    value,
                                })
                        "
                    />
                </p>
                <p v-if="hasChildren" class="copy-sub-campaign__form-item border">
                    <span class="copy-sub-campaign__form-item--label">
                        {{ toSentenceCase($t('planning.copySubCampaign.childrenField')) }}
                    </span>
                    <vuex-checkbox
                        :getter="() => modelsGetter({ model: copySubCampaignModels.childrenModel })"
                        :setter="
                            value =>
                                modelsSetter({ model: copySubCampaignModels.childrenModel, value })
                        "
                    />
                </p>
                <copy-start-dates-list
                    :model-value="modelsGetter({ model: copySubCampaignModels.startDatesModel })"
                    label="planning.copySubCampaign.startDateField"
                    :min-date="minDate"
                    :max-date="maxDate"
                    :max="maxCopyStartDates"
                    allow-multiple
                    @update:model-value="
                        value =>
                            modelsSetter({ model: copySubCampaignModels.startDatesModel, value })
                    "
                />
            </div>
        </template>
    </main-dialog>
</template>

<script setup>
import { computed, onMounted, ref } from 'vue';
import moment from 'moment';
import { useStore } from 'vuex';
import CopySubCampaignModels from '@enums/copy-sub-campaign-models';
import copyLimits from '@enums/copy-limits';
import i18n from '@/js/vue-i18n';

const { maxCopyStartDates } = copyLimits;

const props = defineProps({
    hasActivator: {
        type: Boolean,
        default: true,
    },
    subCampaign: {
        type: Object,
        required: true,
    },
    campaign: {
        type: Object,
        required: true,
    },
});

const store = useStore();
const dialog = ref(null);
const models = ref({
    [CopySubCampaignModels.subCampaignModel]: true,
    [CopySubCampaignModels.childrenModel]: false,
    [CopySubCampaignModels.startDatesModel]: [],
});
const copySubCampaignModels = CopySubCampaignModels;

const validations = computed(() => store.state.clientConfig.validations);
const hasChildren = computed(
    () => !!(props.subCampaign.children && props.subCampaign.children.length)
);
const durationInDays = computed(() =>
    moment(props.subCampaign.endDate)
        .startOf('day')
        .diff(moment(props.subCampaign.startDate).startOf('day'), 'days')
);
const earliestPermittedStartDate = computed(() => {
    const { duration, granularity } = validations.value.subCampaignPlanningPeriod.params;

    return moment
        .utc()
        .add(duration, granularity)
        .startOf('day');
});
const earliestCopyStartDate = computed(() =>
    moment.max(moment(props.campaign.startDate), earliestPermittedStartDate.value)
);
const latestCopyStartDate = computed(() =>
    moment(props.campaign.endDate)
        .startOf('day')
        .subtract(durationInDays.value, 'days')
);
const hasValidCopyWindow = computed(() =>
    earliestCopyStartDate.value.isSameOrBefore(latestCopyStartDate.value, 'day')
);
const copyWindowError = computed(() => {
    if (earliestPermittedStartDate.value.isAfter(latestCopyStartDate.value, 'day')) {
        const { duration, granularity } = validations.value.subCampaignPlanningPeriod.params;

        return {
            messageKey: 'general.errors.subCampaignCopyStartDateTooSoon',
            messageParams: { duration, granularity },
        };
    }

    return {
        messageKey: 'general.errors.subCampaignRestriction',
        messageParams: {},
    };
});
const datepickerFormat = date => i18n.eUTC('dates.default.datepickerFormat', date, moment);
const minDate = computed(() => datepickerFormat(earliestCopyStartDate.value));
const maxDate = computed(() => datepickerFormat(latestCopyStartDate.value));
const defaultStartDate = computed(() =>
    datepickerFormat(moment.max(moment(props.subCampaign.startDate), earliestCopyStartDate.value))
);

const modelsGetter = ({ model }) => models.value[model];
const modelsSetter = ({ model, value }) => {
    models.value[model] = value;
};
const resetModels = () => {
    modelsSetter({
        model: CopySubCampaignModels.startDatesModel,
        value: [defaultStartDate.value],
    });
    modelsSetter({
        model: CopySubCampaignModels.childrenModel,
        value: false,
    });
};
const openDialog = () => {
    dialog.value.open();
};
const closeDialog = () => {
    dialog.value.close();
    resetModels();
};
const copySubCampaignDialogConfirm = async () => {
    const error = await store.dispatch('subCampaigns/copySubCampaign', {
        id: props.subCampaign._id,
        body: {
            startDates: models.value[CopySubCampaignModels.startDatesModel],
            includeChildren: models.value[CopySubCampaignModels.childrenModel],
        },
    });

    if (!error) closeDialog();
};

onMounted(resetModels);

defineExpose({ openDialog });
</script>

<style lang="scss" scoped>
@import '@style/base/_variables.scss';

.copy-sub-campaign {
    font-size: 1.2rem;

    &__description {
        padding: 1rem 0;
    }

    &__error {
        padding-bottom: 1rem;
    }

    &__form-item {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        padding: 1rem 2rem;

        &--label {
            margin-right: 1rem;
            font-weight: bold;
        }
    }

    .border {
        border-bottom: 0.1rem solid $promo-divider-colour;
    }
}
</style>
